/**
 * Les ABSENCES, la CHARGE et l'export des heures prévues (lot PL2, ADR-0015 art. 14) : tout ce qui se décide sans écran.
 *
 * Le serveur est l'autorité : il calcule la charge et la capacité d'une semaine (jours ouvrés non absents × la journée de
 * travail) et refuse une absence qui en chevauche une autre. Ici on LIT ces chiffres pour les dire, on lit la saisie à la
 * française pour donner un message lisible avant l'envoi, et on fabrique le fichier d'export. Aucune règle de calcul n'est
 * recopiée côté écran : l'écran ne recalcule pas la capacité.
 */
import type { AbsenceSemaine, ChargeTechnicien, GenreAbsence, HeuresPrevues } from '../api/client';
import { dureeFr, jourFr } from './planning';

export const GENRES_ABSENCE: readonly GenreAbsence[] = ['LEAVE', 'TRAINING', 'SICK', 'OTHER'];

export const LIBELLE_ABSENCE: Readonly<Record<GenreAbsence, string>> = {
  LEAVE: 'Congé',
  TRAINING: 'Formation',
  SICK: 'Maladie / arrêt',
  OTHER: 'Autre absence',
};

/** L'absence d'un technicien un jour donné (bornes incluses), ou `null`. */
export function absenceDuJour(absences: readonly AbsenceSemaine[], technicianId: string, jour: string): AbsenceSemaine | null {
  return absences.find((a) => a.technicianId === technicianId && a.from <= jour && jour <= a.to) ?? null;
}

// --- La charge d'une semaine ------------------------------------------------------------------------

export type NiveauCharge = 'vide' | 'normale' | 'proche' | 'surcharge';

/** Au-delà de la capacité : surcharge ; à 90 % ou plus : proche. Un choix d'affichage, réglable. */
export const SEUIL_CHARGE_PROCHE = 0.9;

export function niveauCharge(c: ChargeTechnicien): NiveauCharge {
  if (c.minutes <= 0) return 'vide';
  if (c.minutes > c.capaciteMinutes) return 'surcharge';
  return c.minutes >= c.capaciteMinutes * SEUIL_CHARGE_PROCHE ? 'proche' : 'normale';
}

/** « 23 h / 35 h » — la charge prévue sur la capacité. Aucun pourcentage inventé : deux durées, lisibles. */
export function phraseCharge(c: ChargeTechnicien): string {
  return `${dureeFr(c.minutes)} / ${dureeFr(c.capaciteMinutes)}`;
}

/** Ce que la charge ne dit pas : les visites sans durée, et les jours d'absence retirés de la capacité. */
export function precisionsCharge(c: ChargeTechnicien): string[] {
  const p: string[] = [];
  if (c.visitesSansDuree > 0) p.push(`${c.visitesSansDuree} ${c.visitesSansDuree > 1 ? 'visites' : 'visite'} sans durée`);
  if (c.joursAbsents > 0) p.push(`${c.joursAbsents} ${c.joursAbsents > 1 ? 'jours' : 'jour'} d’absence`);
  return p;
}

// --- La saisie d'une absence --------------------------------------------------------------------------

export interface SaisieAbsence {
  readonly technicianId: string;
  readonly kind: GenreAbsence | '';
  readonly from: string;
  readonly to: string;
}

export const SAISIE_ABSENCE_VIDE: SaisieAbsence = { technicianId: '', kind: '', from: '', to: '' };

export type LectureAbsence =
  | { readonly ok: true; readonly corps: { readonly technicianId: string; readonly kind: GenreAbsence; readonly from: string; readonly to: string } }
  | { readonly ok: false; readonly erreur: string };

const JOUR = /^\d{4}-\d{2}-\d{2}$/;

/** Un jour AAAA-MM-JJ qui existe vraiment (le 30 février n'existe pas). */
export function jourValide(jour: string): boolean {
  if (!JOUR.test(jour)) return false;
  const d = new Date(`${jour}T00:00:00.000Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === jour;
}

/** Lit la saisie : un technicien, un type, deux jours réels, la fin au plus tôt le jour du début. Un refus dit le champ. */
export function lireAbsence(saisie: SaisieAbsence): LectureAbsence {
  if (saisie.technicianId === '') return { ok: false, erreur: 'Choisissez le technicien.' };
  if (saisie.kind === '') return { ok: false, erreur: 'Choisissez le type d’absence.' };
  if (!jourValide(saisie.from)) return { ok: false, erreur: 'Le premier jour d’absence est obligatoire.' };
  // Une seule date saisie : l'absence d'un jour.
  const fin = saisie.to === '' ? saisie.from : saisie.to;
  if (!jourValide(fin)) return { ok: false, erreur: 'Le dernier jour d’absence n’est pas une date valide.' };
  if (fin < saisie.from) return { ok: false, erreur: 'Le dernier jour d’absence ne peut pas précéder le premier.' };
  return { ok: true, corps: { technicianId: saisie.technicianId, kind: saisie.kind, from: saisie.from, to: fin } };
}

/** « du 21/10/2026 au 23/10/2026 » ou « le 21/10/2026 ». */
export function phrasePeriode(from: string, to: string): string {
  return from === to ? `le ${jourFr(from)}` : `du ${jourFr(from)} au ${jourFr(to)}`;
}

export function phraseAbsenceCreee(nom: string, kind: GenreAbsence, from: string, to: string): string {
  return `Absence enregistrée pour ${nom} ${phrasePeriode(from, to)} : ${LIBELLE_ABSENCE[kind].toLowerCase()}.`;
}

/**
 * La question posée avant de confier une visite à un technicien ABSENT ce jour-là : jamais un refus, un avertissement
 * (Benjamin l'a voulu : on peut avoir une bonne raison). `null` : rien à dire.
 */
export function avertissementAbsence(absence: AbsenceSemaine | null, nom: string, jour: string): string | null {
  if (absence === null) return null;
  return `${nom} est en absence (${LIBELLE_ABSENCE[absence.kind].toLowerCase()}) le ${jourFr(jour)}. Lui confier quand même cette visite ?`;
}

// --- La capacité d'une journée --------------------------------------------------------------------------

/** « 7 h », « 7,5 h » pour le réglage ; vide si illisible. */
export function heuresDeLaJournee(minutes: number): string {
  const h = minutes / 60;
  return Number.isInteger(h) ? String(h) : String(Math.round(h * 100) / 100).replace('.', ',');
}

/** Lit « 7 », « 7,5 », « 7h30 » en minutes ; `null` si illisible ou hors de 1 h à 24 h (les bornes du serveur). */
export function minutesDeLaJournee(texte: string): number | null {
  const t = texte.trim().toLowerCase().replace(/\s+/g, '');
  if (t === '') return null;
  const hm = /^(\d{1,2})h(\d{1,2})?$/.exec(t);
  let minutes: number;
  if (hm) minutes = Number(hm[1]) * 60 + (hm[2] === undefined ? 0 : Number(hm[2]));
  else if (/^\d{1,2}([.,]\d{1,2})?$/.test(t)) minutes = Math.round(Number(t.replace(',', '.')) * 60);
  else return null;
  return minutes >= 60 && minutes <= 1440 ? minutes : null;
}

// --- L'export des heures prévues ------------------------------------------------------------------------

/** Un champ CSV : jamais une formule (Excel exécuterait « =… », « +… », « -… », « @… »), guillemets doublés. */
export function champCsv(valeur: string): string {
  const neutre = /^[=+\-@\t\r]/.test(valeur) ? `'${valeur}` : valeur;
  return /[";\n\r]/.test(neutre) ? `"${neutre.replace(/"/g, '""')}"` : neutre;
}

/** Des minutes en heures décimales à la française (« 3,5 »). */
export function heuresDecimales(minutes: number): string {
  return String(Math.round((minutes / 60) * 100) / 100).replace('.', ',');
}

/**
 * Le fichier : séparateur « ; » et décimale « , » (Excel en français l'ouvre tel quel), une ligne par technicien et par type.
 * Les heures sont PRÉVUES (les réelles viendront des bons validés) : la colonne le dit. Un jour d'absence n'a pas d'heures.
 */
export function csvHeures(h: HeuresPrevues): string {
  // Registre B34 : les heures RÉALISÉES (pointages) — trajet, sur site, pause, et heures de travail = les trois (la pause est payée).
  const entete = [
    'Technicien',
    'Type',
    'Nature',
    'Heures prévues',
    'Visites ou jours',
    'Visites sans durée',
    'Trajet',
    'Sur site',
    'Pause',
    'Heures de travail',
  ];
  const lignes = h.lignes.map((l) =>
    [
      l.technicianName,
      l.type,
      l.nature === 'visite' ? 'Visites' : 'Absence (jours ouvrés)',
      l.nature === 'visite' ? heuresDecimales(l.minutes) : '',
      String(l.nombre),
      l.nature === 'visite' ? String(l.sansDuree) : '',
      l.nature === 'visite' ? heuresDecimales(l.trajetMinutes) : '',
      l.nature === 'visite' ? heuresDecimales(l.surSiteMinutes) : '',
      l.nature === 'visite' ? heuresDecimales(l.pauseMinutes) : '',
      l.nature === 'visite' ? heuresDecimales(l.travailMinutes) : '',
    ]
      .map(champCsv)
      .join(';'),
  );
  return [entete.join(';'), ...lignes].join('\r\n') + '\r\n';
}

export function nomFichierHeures(from: string, to: string): string {
  return `heures-prevues-${from}-${to}.csv`;
}

/** L'adresse `data:` du fichier (BOM UTF-8 pour Excel) : aucune adresse temporaire du navigateur n'est fabriquée. */
export function adresseCsv(contenu: string): string {
  return `data:text/csv;charset=utf-8,${encodeURIComponent(`\uFEFF${contenu}`)}`;
}

/** La période par défaut de l'export : le mois de `jour` (premier au dernier jour). */
export function moisDe(jour: string): { from: string; to: string } {
  const [a, m] = jour.split('-').map(Number) as [number, number];
  const dernier = new Date(Date.UTC(a, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, '0');
  return { from: `${a}-${mm}-01`, to: `${a}-${mm}-${String(dernier).padStart(2, '0')}` };
}
